import { useState, ChangeEvent, useEffect } from "react"
import {
  SearchNode,
  searchTreeFuzzy,
} from "../features/simulation-decisions/utils"
import { DataColumn, Column } from "./table"
import PropTypes from "prop-types"
import { filteredUsersType } from "../features/simulation/types"
import { useLocation } from "react-router-dom"
import { RootState, useTypedSelector } from "../app/store"

type TableSearchProps = {
  data: {
    check: boolean
    index: number
    usersDetails: DataColumn[]
    usersDetailsRaw?: filteredUsersType
  }[]
  columns: Column[]
  onSearchResults: (
    results: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
      usersDetailsRaw?: filteredUsersType
    }[],
    queryToSearch?: string,
    tabShift?: boolean,
  ) => void
  setSearchText: (text: string) => void
  clearSearch: boolean
  setClearSearch: (value: boolean) => void
}

const TableSearch: React.FC<TableSearchProps> = ({
  data,
  onSearchResults,
  setSearchText,
  clearSearch,
  setClearSearch,
}) => {
  const [query, setQuery] = useState<string>("")
  const [preppedNodes, setPreppedNodes] = useState<SearchNode[]>([])

  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })
  const location = useLocation()
  // Extract the tab from the URL path
  const pathSegments = location.pathname.split("/")
  const tabFromPath = pathSegments[pathSegments.length - 1] || "composite"
  useEffect(() => {
    if (clearSearch) {
      setQuery("")
      setSearchText("")
      onSearchResults(data, "")
      setClearSearch(false)
    }
  }, [clearSearch, data, onSearchResults, setSearchText, setClearSearch])

  // Prepare nodes once when data changes
  useEffect(() => {
    const nodes = data.map(
      (row, idx) =>
        ({
          contents: {
            id: idx,
            text: row.usersDetails.map((col) => col.value).join(" "),
          },
          children: [],
          synonyms: [],
        }) as unknown as SearchNode,
    )
    setPreppedNodes(nodes)
  }, [data])

  useEffect(() => {
    if (authStore.user.roles[0].name === "Recruiter") {
      setQuery("")
      setSearchText("")
      onSearchResults(data, "", true)
    }
  }, [tabFromPath])

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setQuery(query)
    setSearchText(query)

    if (query.length < 2) {
      onSearchResults(data, query)
      return
    }

    const resultIds = searchTreeFuzzy(preppedNodes, query)
    const filteredData = data.filter((_, idx) => resultIds.has(Number(idx)))
    onSearchResults(filteredData, query)
  }

  return (
    <div className="relative w-full md:max-w-[320px]">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <input
        type="text"
        placeholder="Search..."
        value={query}
        onChange={handleSearch}
        className="w-full h-10 pl-10 pr-4 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg placeholder:text-gray-400 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
      />
    </div>
  )
}

TableSearch.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onSearchResults: PropTypes.func.isRequired,
  setSearchText: PropTypes.func.isRequired,
  clearSearch: PropTypes.bool.isRequired,
  setClearSearch: PropTypes.func.isRequired,
}

export default TableSearch
